import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FiDownload, FiEye, FiFileText } from "react-icons/fi";
import { useState } from "react";
import { extractApiErrorDetail } from "../../services/errorHandler";
import {
  downloadEssOnboardingFile,
  openEssOnboardingFileInNewTab,
  useEssDashboard,
} from "../../services/essService";

const formatEssDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
};

function SoftCard({ children, ...rest }) {
  return (
    <Box bg="white" borderRadius="xl" boxShadow="sm" px={{ base: 5, md: 6 }} py={5} {...rest}>
      {children}
    </Box>
  );
}

const EssDocuments = () => {
  const toast = useToast();
  const [docBusyId, setDocBusyId] = useState(null);
  const [docAction, setDocAction] = useState(null);

  const {
    essDashboard,
    essDashboardIsLoading,
    essDashboardError,
    refetchEssDashboard,
    portal,
    linked,
  } = useEssDashboard();

  const errDetail = essDashboardError ? extractApiErrorDetail(essDashboardError) : null;
  const docs = Array.isArray(portal?.onboarding_documents) ? portal.onboarding_documents : [];

  const runDocAction = async (docId, action) => {
    setDocBusyId(docId);
    setDocAction(action);
    try {
      if (action === "view") await openEssOnboardingFileInNewTab(docId);
      else await downloadEssOnboardingFile(docId);
    } catch (e) {
      toast({
        title: "Could not open file",
        description: e?.message || "Try again or use Download.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDocBusyId(null);
      setDocAction(null);
    }
  };

  if (essDashboardIsLoading && !essDashboard) {
    return (
      <Flex justify="center" py={24}>
        <Spinner size="lg" color="blue.500" thickness="3px" />
      </Flex>
    );
  }

  if (essDashboardError && !essDashboard) {
    return (
      <VStack align="stretch" spacing={4} maxW="md">
        <Text color="red.600">{errDetail?.message || "Could not load documents."}</Text>
        <Button size="sm" colorScheme="blue" onClick={() => refetchEssDashboard()}>
          Retry
        </Button>
      </VStack>
    );
  }

  const notLinked = linked === false;

  return (
    <VStack align="stretch" spacing={6} maxW="1100px" pb={10}>
      <Heading size="lg" color="gray.900">
        Documents
      </Heading>
      <Text fontSize="sm" color="gray.600">
        Files shared during onboarding. View or download a copy anytime.
      </Text>

      {notLinked && (
        <SoftCard>
          <Text fontSize="sm" color="gray.600">
            Your account isn’t linked to an employee record yet. Documents will appear here after HR completes setup.
          </Text>
        </SoftCard>
      )}

      {!notLinked && docs.length === 0 && (
        <SoftCard>
          <Text fontSize="sm" color="gray.600">
            No documents uploaded yet. When HR adds files, they’ll show up here.
          </Text>
        </SoftCard>
      )}

      {!notLinked && docs.length > 0 && (
        <VStack align="stretch" spacing={4}>
          {docs.map((doc) => (
            <Box key={doc.id} bg="white" borderRadius="xl" boxShadow="sm" px={5} py={4}>
              <HStack align="flex-start" spacing={4}>
                <Flex
                  w={10}
                  h={10}
                  borderRadius="lg"
                  bg="blue.50"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Icon as={FiFileText} color="blue.600" boxSize={5} />
                </Flex>
                <Box flex={1} minW={0}>
                  <Text fontWeight="700" color="gray.900" noOfLines={2}>
                    {doc.title || doc.file_name || "Document"}
                  </Text>
                  {doc.file_name && doc.file_name !== doc.title && (
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {doc.file_name}
                    </Text>
                  )}
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    {[
                      (doc.uploaded_by_name && String(doc.uploaded_by_name).trim()) ||
                      doc.uploaded_by_email
                        ? `Uploaded by ${(doc.uploaded_by_name && String(doc.uploaded_by_name).trim()) || doc.uploaded_by_email}`
                        : null,
                      formatEssDate(doc.created_at),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </Text>
                </Box>
                <HStack flexShrink={0}>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<FiEye />}
                    borderRadius="lg"
                    isLoading={docBusyId === doc.id && docAction === "view"}
                    isDisabled={!!docBusyId && docBusyId !== doc.id}
                    onClick={() => runDocAction(doc.id, "view")}
                  >
                    View
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<FiDownload />}
                    borderRadius="lg"
                    isLoading={docBusyId === doc.id && docAction === "download"}
                    isDisabled={!!docBusyId && docBusyId !== doc.id}
                    onClick={() => runDocAction(doc.id, "download")}
                  >
                    Download
                  </Button>
                </HStack>
              </HStack>
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default EssDocuments;
